// src/pages/DynamicField/DynamicFieldFormPage.jsx

import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../child/Loder";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import {
  fetchDynamicFieldById,
  createDynamicFieldById,
  updateDynamicFieldById,
  fetchAllDynamicFields,
} from "../../redux/dynamicField/dynamicFieldThunks";
import { clearDynamicField } from "../../redux/dynamicField/dynamicFieldSlice";
import { Icon } from "@iconify/react/dist/iconify.js";

const TYPES = [
  { value: "sexual_orientation", label: "Sexual Orientation" },
  { value: "looking_for", label: "Looking For" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "personality", label: "Personality" },
  { value: "interest", label: "Interest" },
  { value: "living_style", label: "Living Style" },
  { value: "relationship_pace", label: "Relationship Pace" },
  { value: "star_sign", label: "Star Sign" },
  { value: "conflict_style", label: "Conflict Style" },
  { value: "time_availability", label: "Time Availability" },
  { value: "cultural_values", label: "Cultural Values" },
  { value: "political_views", label: "Political Views" },
  { value: "intentional_lifestyle", label: "Intentional Lifestyle" },
  { value: "pets", label: "Pets" },
  { value: "world_views", label: "World Views" },
  
];

const multiSelectTypes = [
  "lifestyle",
  "personality",
  "interest",
  "living_style",
  "conflict_style",
  "cultural_values",
  "political_views",
  "intentional_lifestyle",
  "pets",
];

const DynamicFieldFormPage = () => {
  const { id } = useParams(); // if id exists => edit mode
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { current, loading, error } = useSelector(
    (state) => state.dynamicField
  );

  const [formData, setFormData] = useState({
    type: "",
    subType: "",
    title: "",
    description: "",
    multiSelect: false,
    options: [{ label: "", value: "" }],
    isActive: true,
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false); // ✅ ref to block multiple clicks immediately

  // Load field if editing
  useEffect(() => {
    if (id) {
      dispatch(fetchDynamicFieldById({ id, navigate }));
    }
    return () => dispatch(clearDynamicField());
  }, [dispatch, id, navigate]);

  // Sync redux current to local form
  useEffect(() => {
    if (current) {
      setFormData({
        type: current.type || "",
        subType: current.subType || "",
        title: current.title || "",
        description: current.description || "",
        multiSelect: current.multiSelect || false,
        options:
          current.options && current.options.length > 0
            ? current.options.map((o) => ({ label: o.label, value: o.value })) // 🔑 clone each object
            : [{ label: "", value: "" }],
        isActive: current.isActive ?? true,
      });
    }
  }, [current]);

  // Handle change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error for the field while typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle option change

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...formData.options];

    // Store raw label (exactly what user types)
    updatedOptions[index].label = value;

    // Auto-generate value from label (lowercase, cleaned)
    const cleanValue = value
      .trim()
      .replace(/[^\w\s]/g, "") // remove special characters except letters, numbers, underscore, space
      .replace(/\s+/g, "_") // replace spaces with underscores
      .toLowerCase(); // make lowercase

    updatedOptions[index].value = cleanValue;

    setFormData((prev) => ({ ...prev, options: updatedOptions }));
    // Re-run validation to handle errors
    validateInputs({ ...formData, options: updatedOptions });
  };

  // Add new option
  const addOption = () => {
    const hasEmpty = formData.options.some((opt) => !opt.label.trim());

    if (hasEmpty) {
      setErrors((prev) => ({
        ...prev,
        options: "Please fill in all existing options before adding a new one",
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, { label: "", value: "" }],
    }));
    // Clear previous option error if any
    setErrors((prev) => ({ ...prev, options: "" }));
  };

  // Remove option
  const removeOption = (index) => {
    setFormData((prev) => {
      const updatedOptions = prev.options.filter((_, i) => i !== index);
      const updatedFormData = { ...prev, options: updatedOptions };

      // ✅ Run validation on the new formData immediately
      validateInputs(updatedFormData);

      return updatedFormData;
    });
  };

  const validateInputs = (data = formData) => {
    const newErrors = {};

    if (!data.type) {
      newErrors.type = "Type is required";
    }

    if (!data.title.trim()) {
      newErrors.title = "Title is required";
    } else if (data.title.trim().length < 3) {
      newErrors.title = "Title must be at least 3 characters long";
    }

    if (!data.options || data.options.length === 0) {
      newErrors.options = "At least one option is required";
    } else {
      const optionErrors = data.options.map((opt, index) => {
        if (!opt.label.trim()) {
          return `Option ${index + 1} is required`;
        }
        return "";
      });

      // Normalize labels
      const labels = data.options.map((opt) => opt.label.trim().toLowerCase());

      data.options.forEach((opt, index) => {
        const label = opt.label.trim();

        if (!label) {
          optionErrors[index] = `Option ${index + 1} is required`;
          return;
        }

        // Count duplicates
        const duplicateCount = labels.filter(
          (l) => l === label.toLowerCase()
        ).length;
        if (duplicateCount > 1) {
          optionErrors[index] = "Duplicate option";
        }
      });

      if (optionErrors.some((err) => err)) {
        newErrors.optionFieldErrors = optionErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingRef.current) return;

    // Validate inputs first
    if (!validateInputs()) return;

    submittingRef.current = true; // ✅ block further clicks immediately
    setSubmitting(true); // ✅ update button UI

    let resultAction;
    try {
      if (id) {
        // Edit mode
        resultAction = await dispatch(
          updateDynamicFieldById({ id, data: formData })
        );
      } else {
        // Create mode
        resultAction = await dispatch(
          createDynamicFieldById({ data: formData })
        );
      }

      // ✅ Check for success
      if (
        updateDynamicFieldById.fulfilled.match(resultAction) ||
        createDynamicFieldById.fulfilled.match(resultAction)
      ) {
        setSuccessMessage(
          id
            ? "Preference updated successfully"
            : "Preference created successfully"
        );
        dispatch(fetchAllDynamicFields());
        setTimeout(() => {
          setSuccessMessage("");
          navigate("/preferences");
        }, 800);
      } else {
        const errorMessageResp =
          resultAction?.payload?.message ||
          resultAction?.error?.message ||
          "Something went wrong";
        setErrorMessage(errorMessageResp);
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } finally {
      // ✅ always re-enable button after API finishes
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading && id) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            {successMessage && (
              <SuccessAlert
                message={successMessage}
                onClose={() => setSuccessMessage("")}
              />
            )}
            {/* {error && <DengerAlert message={error} />} */}
            {/* {error && (
              <DengerAlert
                message={typeof error === "string" ? error : error?.message}
                onClose={() => setErrors("")}
              />
            )} */}
            {errorMessage && (
              <DengerAlert
                message={errorMessage}
                onClose={() => setErrorMessage("")}
              />
            )}

            <form onSubmit={handleSubmit} className="row gy-3">
              {/* Type */}
              <div className="col-md-6">
                <label className="form-label">
                  Type <span className="text-danger-600">*</span>
                </label>
                <select
                  name="type"
                  className={`form-select ${errors.type ? "is-invalid" : ""}`}
                  value={formData.type}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    -- Select Type --
                  </option>
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                {errors.type && (
                  <p className="text-danger mt-1">{errors.type}</p>
                )}
              </div>

              {/* SubType */}
              <div className="col-md-6">
                <label className="form-label">SubType</label>
                <input
                  type="text"
                  name="subType"
                  className="form-control"
                  placeholder="Enter SubType"
                  value={formData.subType}
                  onChange={handleChange}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                      .trimStart(); // Prevent leading spaces
                  }}
                />
              </div>

              {/* Title */}
              <div className="col-md-6">
                <label className="form-label">
                  Title <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  placeholder="Enter Type"
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  value={formData.title}
                  onChange={handleChange}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                      .trimStart(); // Prevent leading spaces
                  }}
                />
                {errors.title && (
                  <p className="text-danger mt-1">{errors.title}</p>
                )}
              </div>

              {/* Description */}
              <div className="col-md-6">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  name="description"
                  className="form-control"
                  placeholder="Enter Description"
                  value={formData.description}
                  onChange={handleChange}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                      .trimStart(); // Prevent leading spaces
                  }}
                />
              </div>
              {/* Active/Inactive */}
              <div className="col-md-6">
                <label className="form-label">Status</label>
                <select
                  name="isActive"
                  className="form-select"
                  value={formData.isActive ? "true" : "false"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isActive: e.target.value === "true",
                    }))
                  }
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              {/* MultiSelect */}
              {multiSelectTypes.includes(formData.type) && (
                <div className="col-md-6">
                  <label className="form-label">Options Settings</label>

                  <div className="form-check mt-4 form-control border-0 d-flex align-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="multiSelect"
                      checked={formData.multiSelect}
                      onChange={handleChange}
                    />
                    <label className="form-check-label">
                      Allow MultiSelect
                    </label>
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="col-12">
                <label className="form-label">
                  Options <span className="text-danger-600">*</span>
                </label>
                {formData.options.map((opt, index) => {
                  const hasError =
                    errors.optionFieldErrors?.[index] ||
                    (errors.options &&
                      (!opt.label.trim() || !opt.value.trim()));
                  return (
                    <div
                      key={index}
                      className="d-flex align-items-center mb-2 gap-2"
                    >
                      <input
                        type="text"
                        className={`form-control ${
                          hasError ? "is-invalid" : ""
                        }`}
                        placeholder="Enter Label"
                        value={opt.label}
                        onChange={(e) =>
                          // handleOptionChange(index, "label", e.target.value)
                          handleOptionChange(index, e.target.value)
                        }
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                            .trimStart(); // Prevent leading spaces
                        }}
                      />
                      {/* <input
                        type="text"
                        className={`form-control ${
                          hasError ? "is-invalid" : ""
                        }`}
                        placeholder="Enter Value"
                        value={opt.value}
                        onChange={(e) =>
                          handleOptionChange(index, "value", e.target.value)
                        }
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                            .trimStart(); // Prevent leading spaces
                        }}
                      /> */}
                      <button
                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                        type="button"
                        onClick={() => removeOption(index)}
                        disabled={formData.options.length === 1}
                        style={{
                          border: "none",
                          outline: "none",
                          boxShadow: "none",
                        }}
                      >
                        <Icon
                          icon="ic:baseline-minus"
                          className="icon text-xl line-height-1"
                        />
                      </button>
                    </div>
                  );
                })}
                {errors.options && (
                  <p className="text-danger mt-1">{errors.options}</p>
                )}

                {errors.optionFieldErrors &&
                  errors.optionFieldErrors.map(
                    (err, idx) =>
                      err && (
                        <p key={idx} className="text-danger mb-1">
                          Option {idx + 1}: {err}
                        </p>
                      )
                  )}

                <button
                  className="btn btn-primary text-sm btn-sm px-12 py-12 mt-10 radius-8 d-flex align-items-center gap-2"
                  type="button"
                  onClick={addOption}
                >
                  <Icon
                    icon="ic:baseline-plus"
                    className="icon text-xl line-height-1"
                  />
                  Add Option
                </button>
              </div>

              {/* Submit */}
              <div className="col-12 text-end">
                {/* <button className="btn btn-primary" type="submit">
                  {id ? "Update Field" : "Create Field"}
                </button> */}
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={loading || submitting} // disable during API call
                >
                  {loading || submitting
                    ? "Saving..."
                    : id
                    ? "Update Preference"
                    : "Create Preference"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicFieldFormPage;
